import { $customCode, TemplateCustomCode } from '@loyalty/shared-common/template-api-client';
import {
  Button,
  Dialog,
  DialogActions,
  IconButton,
  Tab,
  Tabs,
  Theme,
  Tooltip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormProvider, useForm } from 'react-hook-form';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCode } from '@fortawesome/pro-solid-svg-icons/faCode';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CodeTab } from './tabs/CodeTab';
import { DEFAULT_JAVASCRIPT } from './default-javascript';
import { useEditorStore } from '../../store';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  dialogActions: {
    padding: spacing(1, 8, 4),
    justifyContent: 'space-between !important',
    marginLeft: '30px',
    marginRight: '30px',
  },
  paper: {
    maxHeight: 'unset !important',
  },
}));

type Tab = 'css' | 'js';

export function CustomCodeModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const classes = useStyles();

  // Use Zustand store
  const customCode = useEditorStore(state => state.customCode);
  const setCustomCode = useEditorStore(state => state.setCustomCode);
  const markAsDirty = useEditorStore(state => state.markAsDirty);

  const [tab, setTab] = useState<Tab>('css');

  const resolver = useJoiResolver($customCode);
  const form = useForm<TemplateCustomCode>({
    defaultValues: customCode,
    resolver,
  });

  const {
    getValues,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = form;

  const handleApply = useCallback(async () => {
    setCustomCode(getValues());
    markAsDirty();
    onClose();
  }, [getValues, setCustomCode, markAsDirty, onClose]);

  useEffect(() => {
    reset(customCode);
  }, [reset, customCode]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={'xl'}
      fullWidth
      classes={{ paper: classes.paper }}
      sx={{
        '& .MuiDialogContent-root': {
          paddingLeft: '20px',
          paddingRight: '20px',
          paddingTop: '0',
          paddingBottom: '2px',
          '&:after': {
            marginTop: 0,
          },
        },
      }}
    >
      <div>
        <IconButton
          onClick={onClose}
          sx={{
            zIndex: 2,
            position: 'absolute',
            right: 5,
            top: 4,
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
        <Tabs value={tab} onChange={(_, value: Tab) => setTab(value)}>
          <Tab
            value="css"
            style={{ pointerEvents: 'auto' }}
            label={
              <>
                {t('customCode.tab.css')}
                {!!errors.css && (
                  <Tooltip title={errors.css.message}>
                    <FontAwesomeIcon color="red" icon={faExclamationCircle} />
                  </Tooltip>
                )}
              </>
            }
            icon={<FontAwesomeIcon icon={faCode} />}
          />
          <Tab
            value="js"
            style={{ pointerEvents: 'auto' }}
            label={
              <>
                {t('customCode.tab.js')}
                {!!errors.js && (
                  <Tooltip title={errors.js.message}>
                    <FontAwesomeIcon color="red" icon={faExclamationCircle} />
                  </Tooltip>
                )}
              </>
            }
            icon={<FontAwesomeIcon icon={faCode} />}
          />
        </Tabs>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(handleApply, console.error)}>
            {tab === 'css' && <CodeTab<TemplateCustomCode> name="css" language="css" />}
            {tab === 'js' && (
              <CodeTab<TemplateCustomCode>
                name="js"
                language="javascript"
                defaultValue={DEFAULT_JAVASCRIPT}
              />
            )}
            <DialogActions className={classes.dialogActions}>
              <Button color="grey" onClick={onClose} startIcon={<FontAwesomeIcon icon={faXmark} />}>
                {t('generic.actions.cancel')}
              </Button>
              <Button
                type="submit"
                startIcon={<FontAwesomeIcon icon={faCheck} />}
                disabled={!isDirty}
                autoFocus
              >
                {t('generic.actions.apply')}
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </div>
    </Dialog>
  );
}
